import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { toast } from 'react-toastify';
import EditorCanvas from './EditorCanvas';
import ElementPalette from './ElementPalette';
import ElementSettingsPanel from './ElementSettingsPanel';
import MessageStylePanel from './MessageStylePanel';
import MyElementsPanel from './MyElementsPanel';
import NameDialog from './NameDialog';
import { useMailEditorConfig } from './mailEditorConfig';
import {
  ELEMENT_TYPE_LABELS,
  cloneElementWithNewId,
  createDefaultElement,
  deepClone,
  newId,
} from './editorUtils';

// Prefer pointer-based collisions so palette drags land in the hovered
// section; fall back to rectangle intersection while sorting.
const collisionDetection = args => {
  const within = pointerWithin(args);
  return within.length > 0 ? within : rectIntersection(args);
};

/*
 * The shared mail "Baukasten": drag & drop canvas plus the sidebar with the
 * basic elements, the message style and the saved elements. Used by both the
 * marketing mail editor and the transactional mail template editor; everything
 * that differs between them comes from the editor config.
 *
 * `onMutate(mutator)` receives a mutator that is applied to a draft copy of the
 * document, so the owning view stays in control of state and dirty tracking.
 */
const MailBlockEditor = ({ doc, onMutate, canEdit }) => {
  const { showFooterBlock, showMessageStyle, showFallbacks, savedElements: savedElementsApi } =
    useMailEditorConfig();

  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('elements');
  const [savedElements, setSavedElements] = useState(null);
  const [savedElementsLoading, setSavedElementsLoading] = useState(false);
  const [saveElementTarget, setSaveElementTarget] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadSavedElements = useCallback(async () => {
    if (!savedElementsApi) return;
    setSavedElementsLoading(true);
    try {
      const data = await savedElementsApi.get();
      if (data) setSavedElements(data.elements ?? data);
    } catch (error) {
      toast.error('Fehler beim Laden der gespeicherten Elemente');
    } finally {
      setSavedElementsLoading(false);
    }
  }, [savedElementsApi]);

  useEffect(() => {
    if (tab === 'myElements' && savedElements === null && !savedElementsLoading) loadSavedElements();
  }, [tab, savedElements, savedElementsLoading, loadSavedElements]);

  /* --------------------------- element mutations --------------------------- */

  const insertElement = useCallback(
    (sectionId, index, element) => {
      onMutate(draft => {
        let section = draft.sections.find(s => s.sectionId === sectionId);
        if (!section) {
          section = { sectionId: sectionId || newId(), elements: [] };
          draft.sections.push(section);
        }
        const at = index === null || index === undefined ? section.elements.length : index;
        section.elements.splice(at, 0, element);
      });
      setSelected({ sectionId, elementId: element.elementId });
    },
    [onMutate]
  );

  // Appends a new element to the last section (creates one if none exists).
  const appendElement = useCallback(
    element => {
      let targetSectionId = doc?.sections[doc.sections.length - 1]?.sectionId;
      if (!targetSectionId) targetSectionId = newId();
      insertElement(targetSectionId, null, element);
    },
    [doc, insertElement]
  );

  const updateSelectedElement = useCallback(
    patch => {
      if (!selected || selected.footer) return;
      onMutate(draft => {
        const section = draft.sections.find(s => s.sectionId === selected.sectionId);
        const element = section?.elements.find(e => e.elementId === selected.elementId);
        if (element) Object.assign(element, patch);
      });
    },
    [selected, onMutate]
  );

  const deleteElementFromCanvas = useCallback(
    (sectionId, elementId) => {
      onMutate(draft => {
        const section = draft.sections.find(s => s.sectionId === sectionId);
        if (section) section.elements = section.elements.filter(e => e.elementId !== elementId);
      });
      setSelected(prev => (prev?.elementId === elementId ? null : prev));
    },
    [onMutate]
  );

  const duplicateElement = useCallback(
    (sectionId, elementId) => {
      let copyId = null;
      onMutate(draft => {
        const section = draft.sections.find(s => s.sectionId === sectionId);
        if (!section) return;
        const index = section.elements.findIndex(e => e.elementId === elementId);
        if (index < 0) return;
        const copy = cloneElementWithNewId(section.elements[index]);
        copyId = copy.elementId;
        section.elements.splice(index + 1, 0, copy);
      });
      if (copyId) setSelected({ sectionId, elementId: copyId });
    },
    [onMutate]
  );

  const addSection = useCallback(() => {
    onMutate(draft => {
      draft.sections.push({ sectionId: newId(), elements: [] });
    });
  }, [onMutate]);

  const deleteSection = useCallback(
    sectionId => {
      onMutate(draft => {
        draft.sections = draft.sections.filter(s => s.sectionId !== sectionId || s.elements.length > 0);
      });
    },
    [onMutate]
  );

  /* ------------------------------ drag & drop ------------------------------ */

  // Resolves a droppable/sortable target to { sectionId, overElementId } where
  // overElementId is null for "append at the end of the section".
  const resolveDropTarget = over => {
    const data = over?.data?.current;
    if (!data) return null;
    if (data.from === 'canvas') return { sectionId: data.sectionId, overElementId: over.id };
    if (data.from === 'section') return { sectionId: data.sectionId, overElementId: null };
    return null;
  };

  const handleDragStart = event => {
    const data = event.active.data.current;
    setActiveDrag({ label: data?.label || ELEMENT_TYPE_LABELS[data?.elementType] || 'Element' });
  };

  const handleDragEnd = event => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || !doc) return;
    const source = active.data.current;
    const target = resolveDropTarget(over);
    if (!source || !target) return;

    const targetSection = doc.sections.find(s => s.sectionId === target.sectionId);
    const targetIndex = target.overElementId
      ? targetSection?.elements.findIndex(e => e.elementId === target.overElementId)
      : null;

    if (source.from === 'palette') {
      insertElement(target.sectionId, targetIndex, createDefaultElement(source.elementType));
      return;
    }
    if (source.from === 'myElements') {
      if (!source.element) return;
      insertElement(target.sectionId, targetIndex, cloneElementWithNewId(source.element));
      return;
    }
    if (source.from === 'canvas') {
      if (active.id === over.id) return;
      onMutate(draft => {
        const fromSection = draft.sections.find(s => s.sectionId === source.sectionId);
        const toSection = draft.sections.find(s => s.sectionId === target.sectionId);
        if (!fromSection || !toSection) return;
        const fromIndex = fromSection.elements.findIndex(e => e.elementId === active.id);
        if (fromIndex < 0) return;
        if (fromSection.sectionId === toSection.sectionId) {
          const toIndex = target.overElementId
            ? toSection.elements.findIndex(e => e.elementId === target.overElementId)
            : toSection.elements.length - 1;
          if (toIndex < 0) return;
          fromSection.elements = arrayMove(fromSection.elements, fromIndex, toIndex);
        } else {
          const [moved] = fromSection.elements.splice(fromIndex, 1);
          const toIndex = target.overElementId
            ? toSection.elements.findIndex(e => e.elementId === target.overElementId)
            : toSection.elements.length;
          toSection.elements.splice(toIndex < 0 ? toSection.elements.length : toIndex, 0, moved);
        }
      });
      setSelected({ sectionId: target.sectionId, elementId: active.id });
    }
  };

  /* ---------------------------- saved elements ---------------------------- */

  const handleSaveElement = async name => {
    if (!saveElementTarget || !savedElementsApi) return;
    try {
      const result = await savedElementsApi.create({ name, element: deepClone(saveElementTarget) });
      if (result !== null) {
        toast.success('Element wurde gespeichert');
        if (savedElements !== null) await loadSavedElements();
      }
    } catch (error) {
      toast.error('Fehler beim Speichern des Elements');
      throw error;
    }
  };

  const handleDeleteSavedElement = async savedElementId => {
    if (!savedElementsApi) return;
    try {
      const result = await savedElementsApi.remove(savedElementId);
      if (result !== null) {
        toast.success('Element wurde gelöscht');
        setSavedElements(prev => (prev || []).filter(entry => entry._id !== savedElementId));
      }
    } catch (error) {
      toast.error('Fehler beim Löschen des Elements');
    }
  };

  const selectedElement = useMemo(() => {
    if (!selected || selected.footer || !doc) return null;
    const section = doc.sections.find(s => s.sectionId === selected.sectionId);
    return section?.elements.find(e => e.elementId === selected.elementId) || null;
  }, [selected, doc]);

  const panelVisible = Boolean(selectedElement) || Boolean(selected?.footer && showFooterBlock);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDrag(null)}
    >
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <EditorCanvas
          doc={doc}
          selected={selected}
          onSelect={setSelected}
          onAddSection={addSection}
          onDeleteSection={deleteSection}
          onDeleteElement={deleteElementFromCanvas}
          onDuplicateElement={duplicateElement}
          onSaveElement={
            savedElementsApi ? element => setSaveElementTarget(deepClone(element)) : null
          }
          canEdit={canEdit}
          showFooterBlock={showFooterBlock}
        />
        <Box
          sx={{
            width: 340,
            flexShrink: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            overflowY: 'auto',
          }}
        >
          {panelVisible ? (
            <ElementSettingsPanel
              key={selected?.footer ? 'footer' : selected?.elementId}
              element={selectedElement}
              footer={doc.footer}
              onElementChange={updateSelectedElement}
              onFooterChange={patch => onMutate(draft => Object.assign(draft.footer, patch))}
              onBack={() => setSelected(null)}
              disabled={!canEdit}
            />
          ) : (
            <>
              <Tabs
                value={tab}
                onChange={(event, next) => setTab(next)}
                variant='fullWidth'
                sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40 }}
              >
                <Tab label='Grundelemente' value='elements' sx={{ minHeight: 40, fontSize: 12, px: 0.5 }} />
                {showMessageStyle && (
                  <Tab label='Nachrichtenstil' value='style' sx={{ minHeight: 40, fontSize: 12, px: 0.5 }} />
                )}
                {savedElementsApi && (
                  <Tab label='Meine Elemente' value='myElements' sx={{ minHeight: 40, fontSize: 12, px: 0.5 }} />
                )}
              </Tabs>
              {tab === 'elements' && (
                <ElementPalette onAdd={type => appendElement(createDefaultElement(type))} disabled={!canEdit} />
              )}
              {tab === 'style' && showMessageStyle && (
                <MessageStylePanel
                  messageStyle={doc.messageStyle}
                  variableFallbacks={doc.variableFallbacks}
                  bookingLink={doc.bookingLink}
                  onStyleChange={patch => onMutate(draft => Object.assign(draft.messageStyle, patch))}
                  onDocChange={patch => onMutate(draft => Object.assign(draft, patch))}
                  disabled={!canEdit}
                  showOuterBackground={showFooterBlock}
                  showFallbacks={showFallbacks}
                />
              )}
              {tab === 'myElements' && savedElementsApi && (
                <MyElementsPanel
                  elements={savedElements || []}
                  loading={savedElementsLoading}
                  onInsert={element => appendElement(cloneElementWithNewId(element))}
                  onDelete={handleDeleteSavedElement}
                  disabled={!canEdit}
                />
              )}
            </>
          )}
        </Box>
      </Box>
      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <Paper elevation={4} sx={{ px: 2, py: 1, opacity: 0.9 }}>
            <Typography variant='body2'>{activeDrag.label}</Typography>
          </Paper>
        ) : null}
      </DragOverlay>
      <NameDialog
        open={Boolean(saveElementTarget)}
        title='Element speichern'
        label='Name des Elements'
        description='Das Element wird unter "Meine Elemente" für alle E-Mails wiederverwendbar gespeichert.'
        defaultValue={saveElementTarget ? ELEMENT_TYPE_LABELS[saveElementTarget.type] : ''}
        onClose={() => setSaveElementTarget(null)}
        onConfirm={handleSaveElement}
      />
    </DndContext>
  );
};

export default MailBlockEditor;
